import React, { Component, PropTypes } from 'react'
import CSSModules from 'react-css-modules'
import styles from './Booking.css'

import { Button, Inputfield } from '../../components'
import { formatDate, getStatus, formatLocationTitle } from '../../utils/format'
import { validEmail } from '../../utils/validate'

const options = {
  allowMultiple: true
}

const allowCancelStatuses = [120, 310, 320] // Status codes when cancelling an order is allowed

@CSSModules(styles, options)
export default class Booking extends Component {
  static propTypes = {
    booking: PropTypes.shape({
      pickUpTimeStamp: PropTypes.string,
      originLocation: PropTypes.shape({
        position: PropTypes.shape({
          latitude: PropTypes.number,
          longitude: PropTypes.number
        }),
        zone: PropTypes.number,
        streetName: PropTypes.string,
        streetNumber: PropTypes.string,
        entrance: PropTypes.string,
        city: PropTypes.string,
        flightInfo: PropTypes.shape({
          flightNumber: PropTypes.string,
          checkedInBaggage: PropTypes.bool
        }),
        locationType: PropTypes.string
      }),
      destinationLocation: PropTypes.shape({
        position: PropTypes.shape({
          latitude: PropTypes.number,
          longitude: PropTypes.number
        }),
        zone: PropTypes.number,
        streetName: PropTypes.string,
        streetNumber: PropTypes.string,
        entrance: PropTypes.string,
        city: PropTypes.string,
        locationType: PropTypes.string
      }),
      traveler: PropTypes.shape({ 
        name: PropTypes.string,
        phoneNumber: PropTypes.string
      }),
      account: {
        number: PropTypes.number,
      },
      accountNumber: PropTypes.number,
      userId: PropTypes.number,
      externalUserId: PropTypes.string,
      serviceType: PropTypes.string,
      vehicleOptions: PropTypes.arrayOf(PropTypes.string),
      id: PropTypes.number,
      bookingCommissionId: PropTypes.string,
      priceComponents: PropTypes.arrayOf(
        PropTypes.shape({
          amount: PropTypes.number,
          name: PropTypes.string,
          type: PropTypes.string
        })
      ),
      createTimeStamp: PropTypes.string,
      bookingStatus: PropTypes.shape({ 
        lastUpdated: PropTypes.string,
        statusCode: PropTypes.number,
        vehicleId: PropTypes.number,
        eta: PropTypes.string,
        vehiclePlate: PropTypes.string
      }),
      bookingType: PropTypes.string,
      receiptStatus: PropTypes.string
    }).isRequired,
    cancelBooking: PropTypes.func,
    sendConfirmation: PropTypes.func,
    showDepartureField: PropTypes.bool
  }

  constructor (props) {
    super(props)
    this.state = {
      email: '',
      error: '',
      isLoading: false,
      open: false
    }

    this._toggleOpen = this._toggleOpen.bind(this)
    this._renderMain = this._renderMain.bind(this)
    this._getAttributes = this._getAttributes.bind(this)
    this._getCarType = this._getCarType.bind(this)
  }

  _toggleOpen () {
    this.setState({
      open: !this.state.open
    })
  }

  _onInputChange (event) {
    const value = event.target.value
    this._validateEmail(value)
    this.setState({ email: value })
  }
  
  _validateEmail (string) {
    let error = this.state.error
    if (string !== '' && !validEmail(string)) {
      error = 'Ogiltig e-postadress.'
    } else {
      error = ''
    }
    this.setState({ error })
  }

  _isButtonDisabled () {
    return (this.state.error !== '' || this.state.isLoading)
  }
    
  _sendConfirmation () {
    this.setState({ isLoading: true })
    this.props.sendConfirmation(this.props.booking.id, this.state.email, this._onSendComplete.bind(this))
  }

  _onSendComplete (success) {
    const state = { isLoading: false }
    if (success) state.email = ''
    this.setState(state)
  }

  _getCarType () {
    const { serviceType, vehicleOptions } = this.props.booking
    let type = ''
    let image = ''
    switch (serviceType) {
      case 'taxi':
        type = 'Taxi'
        image = 'icon-taxi-standard'
        break
      case 'premium':
      case 'business':
        type = 'Business'
        image = 'icon-taxi-premium'
        break
      case 'largeCar':
        type = 'Storbil'
        image = 'icon-taxi-large'
        break
      default:
        break
    }
    if (vehicleOptions && vehicleOptions.length && vehicleOptions.indexOf('largeCar7') !== -1) {
      type = 'Storbil'
      image = 'icon-taxi-large'
    }
    return (
      <div styleName="row carType">
        <img src={`/static/img/cars/${image}@2x.png`} />
        <div styleName="textsmall">{type}</div>
      </div>
    )
  }

  _getAttributes () {
    const attribute = []
    const options = this.props.booking.vehicleOptions
    
    if (options && options.length) {
      options.forEach((option) => {
        switch (option) {
          case 'rearFacingChildSeat':
            attribute.push('bakåtvänd bilbarnstol')
            break
          case 'pet':
            attribute.push('djur')
            break
          case 'boosterSeat1':
            attribute.push('1 bilkudde')
            break
          case 'boosterSeat2':
            attribute.push('2 bilkuddar')
            break
          case 'stationWagon':
            attribute.push('kombi')
            break
          default:
            break
        }
      })
    }

    if (attribute.length === 0) {
      return 'Inga tillval'
    }

    return attribute
      .join(', ')
      .split('. ')
      .map(word => word.substring(0, 1)
      .toUpperCase() + word.substring(1)).join('. ')
  }

  _getBaggage () {
    return this.props.booking.originLocation.flightInfo.checkedInBaggage ? 'Ja' : 'Nej'
  }

  _renderMain () {
    const booking = this.props.booking
    const flightInfo = booking.originLocation.flightInfo
    const destinationText = this.props.showDepartureField ? 'Till' : 'Destination'
    return (
      <div styleName="booking-content">
        <div styleName="booking-content-columns">
          <div styleName="col col45">
            {/* Departure */}
            {this.props.showDepartureField && 
              <div styleName="row">
                <h3>Från</h3>
                <div styleName="textsmall">{formatLocationTitle({ address: booking.originLocation })}</div>
              </div>
            }

            {/* Destination */}
            <div styleName="row">
              <h3>{destinationText}</h3>
              {booking.destinationLocation ?
                <div styleName="textsmall">{formatLocationTitle({ address: booking.destinationLocation })}</div>
                : <div styleName="textsmall">Ej specificerad</div>}
            </div>

            {/* Attributes */} 
            <div styleName="row">
              <h3>Tillval</h3>
              <div styleName="textsmall">{this._getAttributes()}</div>
            </div>
          </div>

          <div styleName="col col30">
            {/* Date created */}
            <div styleName="row">
              <h3>Bokad</h3>
              <div styleName="textsmall">{formatDate(booking.createTimeStamp)}</div>
            </div>

            {/* Account */}
            <div styleName="row">
              <h3>Konto</h3>
              <div styleName="textsmall">{booking.account && booking.account.number && booking.account.number !== 0 ? booking.account.number : '-' }</div>
            </div>

            {/* Flight number */}
            {flightInfo && 
              <div styleName="row">
                <h3>Flightnr.</h3>
                <div styleName="textsmall">{flightInfo.flightNumber}</div>
              </div>
            }
          </div>

          <div styleName="col col25">

            {/* Order number */}
            <div styleName="row">
              <h3>Bokningsnr.</h3>
              <div styleName="textsmall">{booking.id}</div>
            </div>

            {/* Cartype */}
            {this._getCarType()}

            {/* Baggage */}
            {flightInfo && 
              <div styleName="row">
                <h3>Bagage</h3>
                <div styleName="textsmall">{this._getBaggage()}</div>
              </div>
            }
          </div>
        </div>

        {flightInfo && 
          <div styleName="confirmation-form">
            <div styleName="email-field">
              <Inputfield
                errorMessage={this.state.error}
                label="Bokningsbekräftelse"
                onChange={this._onInputChange.bind(this)}
                placeholder="E-postadress"
                value={this.state.email}
              />
            </div>
            <div styleName="send-button">
              <Button 
                disabled={this._isButtonDisabled()}
                onClick={this._sendConfirmation.bind(this)} 
                text="Skicka e-post"
              />
            </div>
          </div>
        }
      </div>
    )
  }

  render () {
    const booking = this.props.booking
    const bookingStatus = booking.bookingStatus
    const flightInfo = booking.originLocation.flightInfo
    const disabled = allowCancelStatuses.indexOf(booking.bookingStatus.statusCode) == -1
    const nameText = flightInfo ? 'Resenär' : 'Rum/namn'
    const pickupText = flightInfo ? 'Landningstid' : 'Hämtningstid'

    return (
      <div styleName="booking">
          <header styleName="header">
            <div styleName="col col45">
              <h2>{nameText}</h2>
              <div styleName="text ellipsis">{booking.traveler.name ? booking.traveler.name : '-' }</div>
            </div>
            <div styleName="col col35">
              <h2>{pickupText}</h2>
              <div styleName="text">{formatDate(booking.pickUpTimeStamp)}</div>
            </div>
            <div styleName="col col20">
              <Button 
                disabled={disabled}
                onClick={this.props.cancelBooking.bind(this, booking.id)} 
                text={booking.bookingStatus.statusCode === 220 ? 'Avbokad' : 'Avboka'} 
              />
            </div>
          </header>
          <div styleName="status-row row">
            <div styleName="col col45">
              <strong>Status:</strong> <span styleName="textsmall">{getStatus(bookingStatus.statusCode)}</span>
            </div>
            <div styleName="col col30">
              <strong>Regnr:</strong> <span styleName="textsmall">{bookingStatus.vehiclePlate == undefined ? '-' : bookingStatus.vehiclePlate}</span>
            </div>
            <div styleName="col col25">
              <strong>Taxinr:</strong> <span styleName="textsmall">{bookingStatus.vehicleId == undefined ? '-' : bookingStatus.vehicleId}</span>
            </div>
          </div>
          {this.state.open && this._renderMain()}
          <div styleName="toggle-row">
            <button onClick={this._toggleOpen} styleName="toggle-button">              
            {this.state.open ? <span><i className="ion-chevron-up" styleName="icon"/>Visa mindre</span> : <span><i className="ion-chevron-down" styleName="icon"/>Visa mer</span>}
            </button>
          </div>
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/Booking/Booking.js